/**
 * Вигляд лівої навігації (UX-02; layout.md, розд. 1 і 3): рейка 64 px при вікні < 1536 px,
 * розгорнута 224 px від 1536 px. Розкладку дає CSS (`@media` + атрибут `data-nav` на `.app`),
 * тож перший кадр правильний без JS і без зсуву. JS потрібен лише кнопці: знати, що зараз видно.
 *
 * Перемикач закріплює вибір. Якщо вибір збігся з тим, що й так дала б ширина вікна, закріплення
 * знімається (`auto`): так без окремого пункту меню можна повернутися до «за шириною вікна».
 */
import { useSyncExternalStore } from 'react';
import type { NavMode } from '../prefs/prefs.ts';

/** Має збігатися з `@media (min-width: 1536px)` у shell.css. */
export const NAV_EXPANDED_FROM = 1536;
const QUERY = `(min-width: ${NAV_EXPANDED_FROM}px)`;

function subscribe(cb: () => void) {
  // jsdom і старі WebKit без matchMedia — запасний шлях через resize.
  const mq = typeof window.matchMedia === 'function' ? window.matchMedia(QUERY) : null;
  if (mq) {
    mq.addEventListener('change', cb);
    return () => mq.removeEventListener('change', cb);
  }
  window.addEventListener('resize', cb);
  return () => window.removeEventListener('resize', cb);
}

function wideNow(): boolean {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia(QUERY).matches
    : window.innerWidth >= NAV_EXPANDED_FROM;
}

/** Чи вікно достатньо широке для розгорнутої навігації за замовчуванням. */
export function useWideWindow(): boolean {
  return useSyncExternalStore(subscribe, wideNow, () => false);
}

/** Збережене значення могло зіпсуватися — невідоме вважаємо `auto`. */
export function normalizeNavMode(mode: unknown): NavMode {
  return mode === 'rail' || mode === 'expanded' ? mode : 'auto';
}

export function isNavExpanded(mode: NavMode, wide: boolean): boolean {
  return mode === 'expanded' || (mode === 'auto' && wide);
}

/** Режим після натискання перемикача (правило — в описі файла). */
export function toggledNavMode(mode: NavMode, wide: boolean): NavMode {
  const expand = !isNavExpanded(mode, wide);
  if (expand === wide) return 'auto';
  return expand ? 'expanded' : 'rail';
}
